/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { action, computed } from '@ember/object';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import AuthService from 'cio/services/auth';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { getLocaleByCode } from 'cio/utils/languages';
import SubscriptionLanguageModel from 'cio/models/subscription-language';
import FormValidations from 'cio/utils/form-validations';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import { MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH } from 'cio/utils/subscription-center';
import { isEmpty } from '@ember/utils';
import ConfirmChangesService from 'cio/services/confirm-changes';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';
import { UserFlags } from 'cio/models/user';

export interface LanguageDropdownOption {
  value: string;
  label: string;
  enabled: boolean;
  isMissingContent: boolean;
}

interface SubscriptionCenterLocalizationEditArgs {
  lang?: string;
  onLangChange: (lang: string) => unknown;
}

const Validations = {
  topics(
    this: SubscriptionCenterLocalizationEdit,
    topics: SubscriptionLanguageModel['topics'],
    model: SubscriptionLanguageModel,
  ) {
    if (model.enabled && model.isTopicsMissingContent) {
      return 'All names and descriptions require a translation';
    }

    if (topics.some(({ name }) => name.length > MAX_NAME_LENGTH)) {
      return `Names can't be longer than ${MAX_NAME_LENGTH} characters`;
    }

    if (model.enabled && topics.some(({ name }) => isEmpty(name))) {
      return `All names must be translated`;
    }

    let missingDescriptions = topics.some(({ id, description }) => {
      if (isEmpty(this.subscriptionCenter.topics.find((topic) => parseInt(topic.id) === id)?.description)) {
        return;
      }

      if (model.enabled && isEmpty(description)) {
        return true;
      }

      return description.length > MAX_DESCRIPTION_LENGTH;
    });

    if (missingDescriptions) {
      return `All descriptions must be translated and can't be longer than ${MAX_DESCRIPTION_LENGTH} characters`;
    }

    return true;
  },
  title(this: SubscriptionCenterLocalizationEdit, title: string, model: SubscriptionLanguageModel) {
    if (title?.length > MAX_DESCRIPTION_LENGTH) {
      return `Header can't be longer than ${MAX_DESCRIPTION_LENGTH} characters`;
    }

    if (model.enabled && !isEmpty(this.defaultHeaderTitle) && isEmpty(title)) {
      return 'Header must be translated';
    }

    return true;
  },

  subtitle(this: SubscriptionCenterLocalizationEdit, subtitle: string, model: SubscriptionLanguageModel) {
    if (subtitle?.length > MAX_DESCRIPTION_LENGTH) {
      return `Headline can't be longer than ${MAX_DESCRIPTION_LENGTH} characters`;
    }

    if (model.enabled && !isEmpty(this.defaultHeaderSubtitle) && isEmpty(subtitle)) {
      return 'Headline must be translated';
    }

    return true;
  },
};

export default class SubscriptionCenterLocalizationEdit extends Component<SubscriptionCenterLocalizationEditArgs> {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service store!: DS.Store;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service confirmChanges!: ConfirmChangesService;
  @service router!: RouterService;

  @tracked isEditing = false;
  @tracked hasSubmitted = false;
  @tracked isConfirmDeleteModalOpen = false;

  validations: FormValidations;

  @computed('args.lang', 'auth.user.userFlags.show_live_language_translation_edit_warning')
  get showLiveEditWarningPopoup() {
    return (
      Boolean(this.selectedLanguage?.enabled) && this.auth.user?.userFlags.show_live_language_translation_edit_warning
    );
  }

  get selectedLanguageId() {
    if (!this.subscriptionCenter.initialized) {
      return '';
    }

    if (this.args.lang != null) {
      return this.args.lang;
    }

    if (this.subscriptionCenter.languages.length > 0) {
      return this.subscriptionCenter.languages[0].id;
    }

    return '';
  }

  get selectedLanguageLocale() {
    return getLocaleByCode(this.selectedLanguageId)?.name ?? 'Unknown language';
  }

  get selectedLanguage() {
    if (!this.subscriptionCenter.initialized) {
      return null;
    }

    return this.subscriptionCenter.languages.find((lang) => lang.id === this.selectedLanguageId);
  }

  get isDirty() {
    if (this.selectedLanguage == null) {
      return false;
    }

    return this.selectedLanguage.isDirty;
  }

  get languageOptions() {
    if (!this.subscriptionCenter.initialized) {
      return [];
    }

    return this.subscriptionCenter.languages.map((lang) => ({
      value: lang.id,
      label: lang.id,
      enabled: lang.enabled,
      isMissingContent: lang.isMissingContent,
    }));
  }

  get defaultTopics() {
    if (!this.subscriptionCenter.initialized) {
      return [];
    }
    return this.subscriptionCenter.topics;
  }

  get toggleStatusDisabled() {
    if (this.selectedLanguage == null) {
      return true;
    }

    if (!this.selectedLanguage.enabled && this.selectedLanguage.isMissingContent) {
      return true;
    }

    return !this.auth.hasPermission('journeys/subscription_center/edit') || this.toggleStatus.isRunning;
  }

  get defaultHeaderTitle() {
    return this.subscriptionCenter.preferencesPage?.header.title ?? '';
  }

  get defaultHeaderSubtitle() {
    return this.subscriptionCenter.preferencesPage?.header.subtitle ?? '';
  }

  constructor(owner: unknown, args: SubscriptionCenterLocalizationEditArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'selectedLanguage',
    });
    this.subscriptionCenter.initializeTask.perform();
    this.confirmChanges.register(this);
  }

  @action
  toggleEditMode() {
    this.isEditing = !this.isEditing;
    this.hasSubmitted = false;

    if (this.isEditing) {
      this.analytics.track('subscription_center', 'language_edit_started');
    }
  }

  @action
  async closePopover(popover: any) {
    if (this.auth.user == null) {
      this.alerts.error(
        'localization.show-live-language-warning.user-missing',
        'Unable to find current user. Refresh the page and try again.',
      );
      return;
    }

    let key: keyof UserFlags = 'show_live_language_translation_edit_warning';
    await this.auth.user.updateUserFlag(key, false);
    popover.close();
    this.isEditing = true;
  }

  @action
  discardChanges() {
    this.toggleEditMode();
    this.selectedLanguage?.rollbackAttributes();
    this.validations.clearErrors();
  }

  @action
  onLangChange(val: LanguageDropdownOption) {
    this.args.onLangChange(val.value);
  }

  @action
  onNameChange(topicId: string, name: string) {
    if (this.selectedLanguage == null) {
      return;
    }

    if (this.selectedLanguage.topicsById[topicId] == null) {
      this.selectedLanguage.set('topics', [...this.selectedLanguage.topics, { id: parseInt(topicId), name }]);
    } else {
      this.selectedLanguage.set(
        'topics',
        this.selectedLanguage.topics.map((topic) => {
          if (topic.id !== parseInt(topicId)) {
            return topic;
          }

          return {
            ...topic,
            name,
          };
        }),
      );
    }

    if (this.hasSubmitted) {
      this.validations.validateAttribute('topics');
    }
  }

  @action
  onDescriptionChange(topicId: string, description: string) {
    if (this.selectedLanguage == null) {
      return;
    }

    if (this.selectedLanguage.topicsById[topicId] == null) {
      this.selectedLanguage.set('topics', [...this.selectedLanguage.topics, { id: parseInt(topicId), description }]);
    } else {
      this.selectedLanguage.set(
        'topics',
        this.selectedLanguage.topics.map((topic) => {
          if (topic.id !== parseInt(topicId)) {
            return topic;
          }

          return {
            ...topic,
            description,
          };
        }),
      );
    }

    if (this.hasSubmitted) {
      this.validations.validateAttribute('topics');
    }
  }

  @action
  onTitleChange(value: string) {
    if (this.selectedLanguage == null) {
      return;
    }

    this.selectedLanguage.title = value;

    if (this.hasSubmitted) {
      this.validations.validateAttribute('title');
    }
  }

  @action
  onSubtitleChange(value: string) {
    if (this.selectedLanguage == null) {
      return;
    }

    this.selectedLanguage.subtitle = value;

    if (this.hasSubmitted) {
      this.validations.validateAttribute('subtitle');
    }
  }

  @action
  onAddLanguage(langs: string[]) {
    this.args.onLangChange(langs.sort()[0]);
  }

  @action
  confirmDeletion() {
    this.isConfirmDeleteModalOpen = true;
  }

  saveLanguage = dropTask(async () => {
    this.hasSubmitted = true;
    // trim whitespace before validation/save
    this.selectedLanguage?.setProperties({
      title: this.selectedLanguage.title?.trim() ?? '',
      subtitle: this.selectedLanguage.subtitle?.trim() ?? '',
      topics: this.selectedLanguage.topics.map((topic) => ({
        ...topic,
        name: topic.name?.trim() ?? '',
        description: topic.description?.trim() ?? '',
      })),
    });

    try {
      await this.validations.validateModel();
    } catch {
      // ignore validation errors, return early
      return;
    }

    try {
      await this.selectedLanguage?.save();
      this.analytics.track('subscription_center', 'language_edited');
      this.isEditing = false;
      this.alerts.success('localization.edit.save.success', `${this.selectedLanguageId} updated`);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  toggleStatus = dropTask(async () => {
    if (this.selectedLanguage == null) {
      return;
    }

    try {
      this.selectedLanguage.enabled = !this.selectedLanguage.enabled;
      await this.selectedLanguage.save();
      this.analytics.track('subscription_center', 'language_status_changed', {
        enabled: this.selectedLanguage.enabled,
      });
      this.alerts.success(
        'localization.edit.language.status-changed',
        `${this.selectedLanguageId} is now ${
          this.selectedLanguage.enabled ? 'visible to' : 'hidden from'
        } your audience`,
      );
    } catch (err) {
      this.selectedLanguage.rollbackAttributes();
      this.apiErrors.report(err);
    }
  });

  deleteLanguage = dropTask(async () => {
    if (this.selectedLanguage == null) {
      return;
    }

    try {
      let serialized: Record<string, any> = this.selectedLanguage.serialize({ includeId: true });
      let currentIndex = this.subscriptionCenter.languages.indexOf(this.selectedLanguage);
      let nextIndex = this.subscriptionCenter.languages.length === currentIndex + 1 ? 0 : currentIndex;

      await this.selectedLanguage.destroyRecord();
      await this.subscriptionCenter.fetchLanguages();
      this.alerts.success(
        'localization.edit.delete.success',
        `${getLocaleByCode(serialized.id)?.name ?? serialized.id} deleted`,
      );
      this.isConfirmDeleteModalOpen = false;
      this.analytics.track('subscription_center', 'language_deleted');

      if (this.subscriptionCenter.languages.length === 0) {
        this.router.transitionTo('workspace-settings.subscription_center.settings');
        return;
      }

      this.args.onLangChange(this.subscriptionCenter.languages[nextIndex].id);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });
}
